(() => {
  'use strict';

  if (window.TotokaCuratedPicks && typeof window.TotokaCuratedPicks.init === 'function') {
    window.TotokaCuratedPicks.init(document);
    return;
  }

  const SELECTOR = '[data-cp-section]';
  const instances = new Map();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const mapRange = (value, start, end) => clamp((value - start) / (end - start));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const easeOut = (value) => 1 - Math.pow(1 - clamp(value), 3);
  const easeInOut = (value) => {
    const t = clamp(value);
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const TIMING = {
    formula: [0.15, 0.32],
    performance: [0.32, 0.52],
    relevance: [0.52, 0.70],
    finalists: [0.70, 0.88],
    finalMessage: [0.895, 0.955],
    path: [0.94, 1]
  };

  const FINAL_SLOTS_DESKTOP = {
    1: { x: 42, y: 46, scale: 1.06, rotation: -1.5 },
    2: { x: 53, y: 38, scale: 1.14, rotation: 0 },
    3: { x: 64, y: 45, scale: 1.06, rotation: 1.5 }
  };

  const FINAL_SLOTS_MOBILE = {
    1: { x: 25, y: 47, scale: 1.01, rotation: -1.5 },
    2: { x: 50, y: 43, scale: 1.07, rotation: 0 },
    3: { x: 75, y: 47, scale: 1.01, rotation: 1.5 }
  };

  const MOBILE_START_SLOTS = [
    { x: 22, y: 48 },
    { x: 51, y: 53 },
    { x: 80, y: 46 },
    { x: 28, y: 75 },
    { x: 57, y: 72 },
    { x: 84, y: 69 }
  ];

  class CuratedPicks {
    constructor(root) {
      this.root = root;
      this.scroller = this.findScrollContainer(root);
      this.track = root.querySelector('[data-cp-intro-track]') || root;
      this.sticky = root.querySelector('.cp-sticky');
      this.hero = root.querySelector('[data-cp-hero]');
      this.products = [...root.querySelectorAll('[data-cp-product]')];
      this.cards = [...root.querySelectorAll('[data-cp-stage]')];
      this.stageShell = root.querySelector('.cp-stages');
      this.final = root.querySelector('[data-cp-final]');
      this.node = root.querySelector('.cp-path-seed__node');
      this.path = root.querySelector('[data-cp-path]');
      this.scrollCue = root.querySelector('[data-cp-scroll-cue]');
      this.backgroundOverlay = root.querySelector('.cp-background__overlay');
      this.backgroundGrain = root.querySelector('.cp-background__grain');
      this.pathExperience = root.querySelector('[data-cp-path-experience]');
      this.curatedPath = root.querySelector('[data-cp-curated-path]');
      this.chapters = [...root.querySelectorAll('[data-cp-chapter]')];
      this.iconsChapter = root.querySelector('[data-cp-icons-chapter]');
      this.iconsProducts = [...root.querySelectorAll('[data-cp-icons-product]')];
      this.iconsCopy = root.querySelector('[data-cp-icons-copy]');
      this.freshChapter = root.querySelector('[data-cp-fresh-chapter]');
      this.freshSlots = [...root.querySelectorAll('[data-cp-fresh-slot]')];
      this.freshCopy = root.querySelector('[data-cp-fresh-copy]');
      this.hypeChapter = root.querySelector('[data-cp-hype-chapter]');
      this.hypeItems = [...root.querySelectorAll('[data-cp-hype-item]')];
      this.hypeCopy = root.querySelector('[data-cp-hype-copy]');
      this.setsChapter = root.querySelector('[data-cp-sets-chapter]');
      this.setsItems = [...root.querySelectorAll('[data-cp-sets-item]')];
      this.setsCopy = root.querySelector('[data-cp-sets-copy]');
      this.setsCard = root.querySelector('[data-cp-sets-card]');
      this.setsLine = root.querySelector('[data-cp-sets-line]');
      this.giftChapter = root.querySelector('[data-cp-gift-chapter]');
      this.giftItems = [...root.querySelectorAll('[data-cp-gift-item]')];
      this.giftDecor = [...root.querySelectorAll('[data-cp-gift-decor]')];
      this.giftCopy = root.querySelector('[data-cp-gift-copy]');
      this.pathFinalChapter = root.querySelector('[data-cp-path-final-chapter]');
      this.pathFinalNode = root.querySelector('[data-cp-path-final-node]');
      this.pathFinalContent = root.querySelector('[data-cp-path-final-content]');
      this.pathFinalParts = [...root.querySelectorAll('[data-cp-path-final-part]')];
      this.mediaReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileMedia = window.matchMedia('(max-width: 989px)');
      this.scrollTrigger = null;
      this.heroTimeline = null;
      this.timelineClock = null;
      this.usesScrollTrigger = false;
      this.resizeObserver = null;
      this.raf = null;
      this.progress = 0;
      this.destroyed = false;
      this.excludedProducts = new Set();
      this.wasMobile = this.mobileMedia.matches;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);

      this.captureProductSettings();
      this.init();
    }

    findScrollContainer(element) {
      let parent = element.parentElement;

      while (parent && parent !== document.documentElement) {
        const styles = getComputedStyle(parent);
        if (/(auto|scroll|overlay)/.test(styles.overflowY)) return parent;
        parent = parent.parentElement;
      }

      return window;
    }

    scrollViewportHeight() {
      return this.scroller === window
        ? (window.innerHeight || document.documentElement.clientHeight)
        : this.scroller.clientHeight;
    }

    scrollViewportTop() {
      return this.scroller === window ? 0 : this.scroller.getBoundingClientRect().top;
    }

    captureProductSettings() {
      const rawData = this.products.map((product, index) => {
        const styles = getComputedStyle(product);
        return {
          index,
          element: product,
          outcome: product.dataset.outcome || '',
          finalSlot: product.dataset.finalSlot || '2',
          depth: Number(product.dataset.depth || 50) / 100,
          x: parseFloat(styles.getPropertyValue('--cp-x')) || 68,
          y: parseFloat(styles.getPropertyValue('--cp-y')) || 50,
          rotation: parseFloat(styles.getPropertyValue('--cp-rotation')) || 0
        };
      });

      const resolvedOutcomes = this.resolveProductOutcomes(rawData);
      const usedFinalSlots = new Set();
      let finalistOrder = 0;

      this.productData = rawData.map((data, index) => {
        const outcome = resolvedOutcomes[index] || 'finalist';
        let finalSlot = data.finalSlot;

        if (outcome === 'finalist') {
          finalistOrder += 1;
          if (!['1', '2', '3'].includes(finalSlot) || usedFinalSlots.has(finalSlot)) {
            finalSlot = ['1', '2', '3'].find((slot) => !usedFinalSlots.has(slot)) || String(Math.min(finalistOrder, 3));
          }
          usedFinalSlots.add(finalSlot);
        }

        data.element.dataset.cpResolvedOutcome = outcome;
        data.element.dataset.cpResolvedFinalSlot = finalSlot;
        return { ...data, outcome, finalSlot };
      });
    }

    resolveProductOutcomes(products) {
      const allowed = new Set(['finalist', 'formula', 'performance', 'relevance']);
      const outcomes = products.map((data) => allowed.has(data.outcome) ? data.outcome : '');
      const count = (name) => outcomes.filter((outcome) => outcome === name).length;
      const hasCompleteEightProductModel = products.length === 8
        && count('finalist') === 3
        && count('formula') === 2
        && count('performance') === 2
        && count('relevance') === 1;

      if (hasCompleteEightProductModel || products.length !== 8) {
        return outcomes.map((outcome) => outcome || 'finalist');
      }

      /*
       * Shopify can retain older blocks without selection_outcome values.
       * Keep explicit choices where possible, then complete a guaranteed
       * 8 -> 6 -> 4 -> 3 model without changing the Theme Editor schema.
       */
      const resolved = new Array(products.length).fill('');
      const claim = (name, amount) => {
        let claimed = 0;

        outcomes.forEach((outcome, index) => {
          if (claimed < amount && !resolved[index] && outcome === name) {
            resolved[index] = name;
            claimed += 1;
          }
        });

        resolved.forEach((outcome, index) => {
          if (claimed < amount && !outcome) {
            resolved[index] = name;
            claimed += 1;
          }
        });
      };

      claim('finalist', 3);
      claim('formula', 2);
      claim('performance', 2);
      claim('relevance', 1);
      return resolved;
    }

    init() {
      this.bindMediaEvents();

      if (this.mediaReduced.matches) {
        this.clearMotionStyles();
        this.root.classList.add('is-ready', 'is-reduced-motion');
        if (this.curatedPath) this.curatedPath.style.strokeDashoffset = '0';
        return;
      }

      this.root.classList.remove('is-reduced-motion');
      this.setupDriver();
      this.update(0);
      requestAnimationFrame(() => this.root.classList.add('is-ready'));
    }

    bindMediaEvents() {
      const add = (query, handler) => {
        if (query.addEventListener) query.addEventListener('change', handler);
        else query.addListener(handler);
      };
      add(this.mediaReduced, this.handleMotionChange);
      add(this.mobileMedia, this.handleResize);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.root);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }
    }

    setupDriver() {
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger || (gsap && gsap.plugins && gsap.plugins.ScrollTrigger);

      if (gsap && ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
        this.root.classList.add('is-scrolltrigger-driven');
        this.timelineClock = { value: 0 };
        this.heroTimeline = gsap.timeline({
          defaults: { ease: 'none' },
          onUpdate: () => this.handleScroll(),
          scrollTrigger: {
            trigger: this.track,
            scroller: this.scroller === window ? undefined : this.scroller,
            start: 'top top',
            end: () => `+=${Math.max(1, this.track.offsetHeight - this.scrollViewportHeight())}`,
            pin: this.sticky,
            pinSpacing: false,
            scrub: 0.4,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: () => this.handleScroll(),
            onRefresh: () => this.handleScroll()
          }
        });
        this.buildHeroTimeline(gsap);
        this.scrollTrigger = this.heroTimeline.scrollTrigger;
        this.usesScrollTrigger = true;
      }

      this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
      this.handleScroll();
    }

    buildHeroTimeline(gsap) {
      if (!this.heroTimeline) return;

      const timeline = this.heroTimeline;
      const mobile = this.mobileMedia.matches;
      const slots = mobile ? FINAL_SLOTS_MOBILE : FINAL_SLOTS_DESKTOP;

      /* A 100-unit clock keeps every percentage aligned to the pinned scene. */
      timeline.to(this.timelineClock, { value: 1, duration: 100 }, 0);

      this.productData.forEach((data) => {
        const product = data.element;
        const mobileStart = mobile && MOBILE_START_SLOTS[data.index];
        const startX = mobileStart ? mobileStart.x : data.x;
        const startY = mobileStart ? mobileStart.y : data.y;

        gsap.set(product, {
          left: `${startX}%`,
          top: `${startY}%`,
          x: 0,
          y: 0,
          xPercent: -50,
          yPercent: -50,
          z: data.depth * 60,
          rotation: data.rotation,
          scale: 1 + data.depth * 0.035,
          opacity: 1,
          visibility: 'visible',
          force3D: true
        });

        if (data.outcome === 'finalist') {
          const target = slots[data.finalSlot] || slots[2];
          timeline.to(product, {
            left: `${target.x}%`,
            top: `${target.y}%`,
            z: 110,
            rotation: target.rotation,
            scale: target.scale,
            duration: 18,
            ease: 'power2.inOut'
          }, TIMING.finalists[0] * 100);
          return;
        }

        const [stageStart, stageEnd] = TIMING[data.outcome];
        const exitStart = (stageStart + 0.035) * 100;
        const exitEnd = Math.min(stageEnd, stageStart + 0.12) * 100;
        const horizontalDirection = startX >= (mobile ? 50 : 66) ? 1 : -1;
        const verticalDirection = startY >= 53 ? 1 : -1;

        timeline.to(product, {
          x: horizontalDirection * (mobile ? 52 : 118),
          y: verticalDirection * (mobile ? 34 : 62),
          z: -170,
          scale: 0.76,
          opacity: 0,
          visibility: 'hidden',
          duration: exitEnd - exitStart,
          ease: 'power2.inOut'
        }, exitStart);
      });

      if (this.stageShell) {
        timeline.fromTo(this.stageShell, {
          opacity: 0,
          visibility: 'hidden',
          y: 9
        }, {
          opacity: 1,
          visibility: 'visible',
          y: 0,
          duration: 3.5,
          ease: 'power2.out'
        }, TIMING.formula[0] * 100);

        timeline.to(this.stageShell, {
          opacity: 0,
          visibility: 'hidden',
          y: -7,
          duration: 3.5,
          ease: 'power2.inOut'
        }, TIMING.relevance[1] * 100 - 3.5);
      }

      this.cards.forEach((card, index) => {
        const timing = TIMING[card.dataset.cpStage];
        if (!timing) return;
        const enterAt = Math.max(TIMING.formula[0], timing[0] - (index === 0 ? 0 : 0.015)) * 100;
        const exitAt = (timing[1] - 0.015) * 100;

        timeline.fromTo(card, {
          opacity: 0,
          visibility: 'hidden',
          y: 5
        }, {
          opacity: 1,
          visibility: 'visible',
          y: 0,
          duration: index === 0 ? 2.5 : 3,
          ease: 'power2.out'
        }, enterAt);

        timeline.to(card, {
          opacity: 0,
          visibility: 'hidden',
          y: -4,
          duration: card.dataset.cpStage === 'relevance' ? 1.5 : 3,
          ease: 'power2.inOut'
        }, exitAt);
      });
    }

    handleScroll() {
      if (this.raf || this.destroyed) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        let introProgress;

        if (this.heroTimeline && typeof this.heroTimeline.progress === 'function') {
          introProgress = this.heroTimeline.progress();
        } else if (this.scrollTrigger && typeof this.scrollTrigger.progress === 'number') {
          introProgress = this.scrollTrigger.progress;
        } else {
          const rect = this.track.getBoundingClientRect();
          const distance = Math.max(1, this.track.offsetHeight - this.scrollViewportHeight());
          introProgress = (this.scrollViewportTop() - rect.top) / distance;
        }

        this.update(clamp(introProgress));
        this.updateCuratedPath();
      });
    }

    handleResize() {
      if (this.destroyed || this.mediaReduced.matches) return;

      if (this.wasMobile !== this.mobileMedia.matches) {
        const root = this.root;
        this.destroy();
        initSection(root);
        return;
      }

      this.captureProductSettings();
      this.update(this.progress);
      this.updateCuratedPath();
      if (this.scrollTrigger && this.scrollTrigger.refresh) this.scrollTrigger.refresh();
      else this.handleScroll();
    }

    handleMotionChange() {
      this.destroy();
      initSection(this.root);
    }

    update(progress) {
      this.progress = clamp(progress);
      this.root.style.setProperty('--cp-progress', this.progress.toFixed(4));
      this.updateHero();
      if (this.heroTimeline) {
        this.syncExcludedProductLocks();
      } else {
        this.updateProducts();
        this.updateCards();
      }
      this.updateFinal();
      this.updateCue();
    }

    syncExcludedProductLocks() {
      this.productData.forEach((data) => {
        if (data.outcome === 'finalist' || !TIMING[data.outcome]) return;
        const [stageStart, stageEnd] = TIMING[data.outcome];
        const exitEnd = Math.min(stageEnd, stageStart + 0.12);

        if (this.progress >= exitEnd || this.excludedProducts.has(data.element)) {
          this.excludedProducts.add(data.element);
          data.element.classList.add('is-excluded');
          data.element.setAttribute('aria-hidden', 'true');
        }
      });
    }

    updateHero() {
      const fade = easeInOut(mapRange(this.progress, 0.72, 0.87));
      const clean = easeInOut(mapRange(this.progress, 0.7, 0.88));
      const drift = lerp(0, -24, fade);
      this.hero.style.opacity = String(1 - fade);
      this.hero.style.transform = this.mobileMedia.matches
        ? `translate3d(0, ${drift}px, 0)`
        : `translate3d(0, calc(-50% + ${drift}px), 0)`;

      if (this.backgroundOverlay) this.backgroundOverlay.style.opacity = lerp(1, 0.76, clean).toFixed(3);
      if (this.backgroundGrain) this.backgroundGrain.style.opacity = lerp(0.15, 0.07, clean).toFixed(3);
    }

    exclusionProgress(outcome) {
      if (!TIMING[outcome]) return 0;
      const [start, end] = TIMING[outcome];
      return easeInOut(mapRange(this.progress, start + 0.035, Math.min(end, start + 0.12)));
    }

    updateProducts() {
      const finalProgress = easeInOut(mapRange(this.progress, ...TIMING.finalists));
      const slots = this.mobileMedia.matches ? FINAL_SLOTS_MOBILE : FINAL_SLOTS_DESKTOP;
      const motionScale = this.mobileMedia.matches ? 0.48 : 1;
      const floatFade = 1 - easeInOut(mapRange(this.progress, 0.12, 0.24));

      this.productData.forEach((data) => {
        const product = data.element;
        const isFinalist = data.outcome === 'finalist';
        let exclusion = isFinalist ? 0 : this.exclusionProgress(data.outcome);

        if (!isFinalist && exclusion >= 0.999) this.excludedProducts.add(product);
        if (this.excludedProducts.has(product)) exclusion = 1;
        const parallax = this.progress * (data.depth - 0.5) * 26 * motionScale;
        const floatWave = (Math.sin(this.progress * Math.PI * 2.4 + data.index * 0.72) - Math.sin(data.index * 0.72)) * 2.2 * floatFade * motionScale;
        const mobileStart = this.mobileMedia.matches && MOBILE_START_SLOTS[data.index];
        const originY = mobileStart ? mobileStart.y : data.y;
        let x = mobileStart ? mobileStart.x : data.x;
        let y = originY + parallax + floatWave;
        let rotation = data.rotation;
        let scale = 1 + data.depth * 0.035;
        let opacity = 1;
        let z = data.depth * 60;

        if (isFinalist) {
          const target = slots[data.finalSlot] || slots[2];
          x = lerp(x, target.x, finalProgress);
          y = lerp(y, target.y, finalProgress);
          rotation = lerp(rotation, target.rotation, finalProgress);
          scale = lerp(scale, target.scale, finalProgress);
          z = lerp(z, 110, finalProgress);
        } else {
          const horizontalDirection = x >= (this.mobileMedia.matches ? 50 : 66) ? 1 : -1;
          const verticalDirection = originY >= 53 ? 1 : -1;
          x += exclusion * horizontalDirection * 8.5 * motionScale;
          scale = lerp(scale, 0.76, exclusion);
          opacity = lerp(1, 0, exclusion);
          y += exclusion * verticalDirection * 7.5 * motionScale;
          z = lerp(z, -170, exclusion);
        }

        product.style.left = `${x}%`;
        product.style.top = `${y}%`;
        product.style.opacity = opacity.toFixed(3);
        product.style.transform = `translate3d(-50%, -50%, ${z.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        product.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';
        product.classList.toggle('is-excluded', exclusion >= 0.999);
        product.setAttribute('aria-hidden', exclusion >= 0.999 ? 'true' : 'false');
      });
    }

    updateCards() {
      const firstStage = TIMING.formula[0];
      const lastStage = TIMING.relevance[1];
      const shellEnter = easeOut(mapRange(this.progress, firstStage, firstStage + 0.025));
      const shellExit = 1 - easeInOut(mapRange(this.progress, lastStage - 0.025, lastStage));
      const shellVisibility = Math.min(shellEnter, shellExit);

      if (this.stageShell) {
        const shellY = lerp(9, 0, shellEnter) - lerp(0, 7, 1 - shellExit);
        this.stageShell.style.opacity = shellVisibility.toFixed(3);
        this.stageShell.style.transform = `translate3d(0, ${shellY.toFixed(1)}px, 0)`;
        this.stageShell.style.visibility = shellVisibility < 0.01 ? 'hidden' : 'visible';
      }

      this.cards.forEach((card, index) => {
        const timing = TIMING[card.dataset.cpStage];
        if (!timing) return;
        const enterStart = Math.max(firstStage, timing[0] - (index === 0 ? 0 : 0.015));
        const enterEnd = timing[0] + 0.015;
        const exitStart = timing[1] - 0.015;
        const exitEnd = Math.min(lastStage, timing[1] + (card.dataset.cpStage === 'relevance' ? 0 : 0.015));
        const enter = easeOut(mapRange(this.progress, enterStart, enterEnd));
        const exitProgress = easeInOut(mapRange(this.progress, exitStart, exitEnd));
        const exit = 1 - exitProgress;
        const visibility = Math.min(enter, exit);
        card.style.opacity = visibility.toFixed(3);
        card.style.transform = `translate3d(0, ${(lerp(5, 0, enter) - lerp(0, 4, exitProgress)).toFixed(1)}px, 0)`;
        card.style.visibility = visibility < 0.01 ? 'hidden' : 'visible';
      });
    }

    updateFinal() {
      const message = easeOut(mapRange(this.progress, ...TIMING.finalMessage));
      const path = easeInOut(mapRange(this.progress, ...TIMING.path));
      const node = easeOut(mapRange(this.progress, 0.915, 0.965));

      this.final.style.opacity = message.toFixed(3);
      this.final.style.transform = `translate3d(-50%, ${lerp(18, 0, message).toFixed(1)}px, 0)`;
      this.node.style.opacity = node.toFixed(3);
      this.node.style.transform = `scale(${lerp(0.3, 1, node).toFixed(3)})`;
      this.path.style.strokeDashoffset = String(1 - path);
    }

    updateCue() {
      const opacity = 0.68 * (1 - easeInOut(mapRange(this.progress, 0.04, 0.14)));
      this.scrollCue.style.opacity = opacity.toFixed(3);
    }

    updateCuratedPath() {
      if (!this.pathExperience || !this.curatedPath) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const rect = this.pathExperience.getBoundingClientRect();
      const start = viewportHeight;
      const distance = Math.max(1, rect.height);
      const rawProgress = clamp((start - rect.top) / distance);
      const drawProgress = rawProgress;
      const phasePresence = (center, radius) => easeInOut(1 - clamp(Math.abs(drawProgress - center) / radius));
      const coolPresence = phasePresence(0.25, 0.2);
      const energyPresence = phasePresence(0.42, 0.19);
      const structurePresence = phasePresence(0.59, 0.19);
      const warmPresence = phasePresence(0.76, 0.2);
      const clearPresence = easeOut(mapRange(drawProgress, 0.84, 1));
      const lightX = 70 - coolPresence * 16 + energyPresence * 8 - warmPresence * 5 + clearPresence * 3;
      const shiftX = lerp(0.75, -0.85, drawProgress);
      const shiftY = lerp(-0.35, 0.45, drawProgress);
      const coolOpacity = 0.08 + coolPresence * 0.2 + energyPresence * 0.12;
      const warmOpacity = warmPresence * 0.34;
      const depthOpacity = 0.1 + energyPresence * 0.13 + structurePresence * 0.18;
      const lightOpacity = 0.24 + clearPresence * 0.34;

      this.curatedPath.style.strokeDashoffset = (1 - drawProgress).toFixed(4);
      this.pathExperience.style.setProperty('--cp-path-progress', drawProgress.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-cool', coolPresence.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-energy', energyPresence.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-structure', structurePresence.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-warm', warmPresence.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-clear', clearPresence.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-cool-opacity', coolOpacity.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-warm-opacity', warmOpacity.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-depth-opacity', depthOpacity.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-light-opacity', lightOpacity.toFixed(4));
      this.pathExperience.style.setProperty('--cp-world-light-x', `${lightX.toFixed(2)}%`);
      this.pathExperience.style.setProperty('--cp-world-shift-x', `${shiftX.toFixed(3)}vw`);
      this.pathExperience.style.setProperty('--cp-world-shift-y', `${shiftY.toFixed(3)}vh`);
      this.pathExperience.style.setProperty('--cp-world-depth-x', `${(-shiftX * 0.65).toFixed(3)}vw`);
      this.pathExperience.style.setProperty('--cp-world-depth-y', `${(-shiftY * 0.5).toFixed(3)}vh`);

      this.chapters.forEach((chapter, index) => {
        const chapterRect = chapter.getBoundingClientRect();
        const center = chapterRect.top + chapterRect.height * 0.5;
        const nodeTarget = (index + 0.5) / Math.max(1, this.chapters.length);
        const pathReached = drawProgress >= nodeTarget - 0.012;
        const isActive = pathReached && center >= viewportHeight * 0.2 && center <= viewportHeight * 0.84;
        const node = chapter.querySelector('.cp-chapter__node:not(.cp-chapter__node--final)');

        chapter.classList.toggle('is-path-reached', pathReached);
        chapter.classList.toggle('is-active', isActive);

        if (node) {
          const progress = this.chapterProgress(chapter, viewportHeight);
          const pulseIn = easeOut(mapRange(progress, 0.22, 0.32));
          const pulseOut = 1 - easeInOut(mapRange(progress, 0.32, 0.48));
          const pulse = Math.min(pulseIn, pulseOut);
          const nodeScale = isActive ? lerp(1, 1.05, pulse) : pathReached ? 0.84 : 0.72;
          node.style.transform = `translate(-50%, -50%) scale(${nodeScale.toFixed(3)})`;
          node.style.setProperty('--cp-node-ring-opacity', (pulse * 0.3).toFixed(3));
          node.style.setProperty('--cp-node-halo-opacity', (pulse * 0.1).toFixed(3));
        }
      });

      this.updateIconsChapter(viewportHeight);
      this.updateFreshChapter(viewportHeight);
      this.updateHypeChapter(viewportHeight);
      this.updateSetsChapter(viewportHeight);
      this.updateGiftChapter(viewportHeight);
      this.updatePathFinalChapter(viewportHeight);
    }

    chapterProgress(chapter, viewportHeight) {
      if (!chapter) return 0;
      const node = chapter.querySelector('.cp-chapter__node');
      const referenceRect = (node || chapter).getBoundingClientRect();
      const referenceCenter = referenceRect.top + referenceRect.height * 0.5;
      const entryPoint = viewportHeight * 0.98;
      const travel = Math.max(1, viewportHeight * 0.46);
      return clamp((entryPoint - referenceCenter) / travel);
    }

    updateCopySequence(copy, progress, start, gap = 0.055, duration = 0.18) {
      if (!copy) return;

      const mobile = this.mobileMedia.matches;
      const localGap = mobile ? gap * 0.7 : gap;
      const localDuration = mobile ? duration * 0.72 : duration;
      const parts = [...copy.children];

      copy.style.opacity = '1';
      copy.style.visibility = 'visible';
      copy.style.transform = mobile ? 'translate3d(0, 0, 0)' : 'translate3d(0, -50%, 0)';

      parts.forEach((part, index) => {
        const partStart = start + index * localGap;
        const entrance = easeOut(mapRange(progress, partStart, partStart + localDuration));
        part.style.opacity = entrance.toFixed(3);
        part.style.visibility = entrance < 0.01 ? 'hidden' : 'visible';
        part.style.transform = `translate3d(0, ${lerp(mobile ? 16 : 24, 0, entrance).toFixed(1)}px, 0)`;
        if (part.matches('a')) part.style.pointerEvents = entrance < 0.5 ? 'none' : 'auto';
      });
    }

    updateIconsChapter(viewportHeight) {
      if (!this.iconsChapter || !this.iconsProducts.length) return;

      const progress = this.chapterProgress(this.iconsChapter, viewportHeight);
      const definitions = this.mobileMedia.matches
        ? {
            lead: { start: 0.1, end: 0.38, x: 0, y: 34, z: -48, scale: 0.96, rotation: 0, drift: -2 },
            'secondary-one': { start: 0.18, end: 0.44, x: -7, y: 26, z: -38, scale: 0.96, rotation: -2, drift: 2 },
            'secondary-two': { start: 0.24, end: 0.5, x: 7, y: 22, z: -34, scale: 0.97, rotation: 2, drift: -2 }
          }
        : {
            lead: { start: 0.1, end: 0.4, x: 0, y: 82, z: -90, scale: 0.94, rotation: 0, drift: -3 },
            'secondary-one': { start: 0.18, end: 0.46, x: -16, y: 70, z: -65, scale: 0.95, rotation: -2.5, drift: 3 },
            'secondary-two': { start: 0.24, end: 0.52, x: 15, y: 64, z: -58, scale: 0.95, rotation: 2.5, drift: -2.5 }
          };

      this.iconsProducts.forEach((product) => {
        const definition = definitions[product.dataset.cpIconsRole] || definitions.lead;
        const visual = product.firstElementChild;
        const entrance = easeOut(mapRange(progress, definition.start, definition.end));
        const depth = lerp(definition.z, 0, entrance);
        const horizontal = lerp(definition.x, 0, entrance);
        const vertical = lerp(definition.y, 0, entrance) + (progress - 0.5) * definition.drift;
        const rotation = lerp(definition.rotation, 0, entrance);
        const scale = lerp(definition.scale, 1, entrance);

        product.style.opacity = entrance.toFixed(3);
        product.style.visibility = entrance < 0.01 ? 'hidden' : 'visible';
        product.style.transform = `translate3d(calc(-50% + ${horizontal.toFixed(1)}px), calc(-50% + ${vertical.toFixed(1)}px), ${depth.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        if (visual) {
          visual.style.opacity = entrance.toFixed(3);
          visual.style.visibility = entrance < 0.01 ? 'hidden' : 'visible';
        }
      });

      this.updateCopySequence(this.iconsCopy, progress, 0.26, 0.032, 0.16);
    }

    updateFreshChapter(viewportHeight) {
      if (!this.freshChapter || !this.freshSlots.length) return;

      const progress = this.chapterProgress(this.freshChapter, viewportHeight);
      const definitions = [
        { start: 0.1, end: 0.4, y: 64, z: -74, scale: 0.94, rotation: 0, drift: -3, direction: 1 },
        { start: 0.17, end: 0.45, y: 54, z: -54, scale: 0.95, rotation: -2, drift: 3, direction: -1 },
        { start: 0.22, end: 0.49, y: 58, z: -58, scale: 0.95, rotation: 2, drift: -2.5, direction: -1 },
        { start: 0.27, end: 0.53, y: 46, z: -48, scale: 0.96, rotation: 1.5, drift: 2.5, direction: 1 }
      ];

      this.freshSlots.forEach((slot, index) => {
        const definition = definitions[index] || definitions[definitions.length - 1];
        const product = slot.querySelector('[data-cp-fresh-product]');
        const panel = slot.querySelector('[data-cp-fresh-panel]');
        const opening = easeInOut(mapRange(progress, definition.start, definition.end));
        const productEntrance = easeOut(mapRange(progress, definition.start + 0.03, definition.end + 0.05));
        const vertical = lerp(definition.y, 0, productEntrance) + (progress - 0.5) * definition.drift;
        const depth = lerp(definition.z, 0, productEntrance);
        const rotation = lerp(definition.rotation, 0, productEntrance);
        const scale = lerp(definition.scale, 1, productEntrance);

        if (product) {
          const visual = product.firstElementChild;
          const clipInset = lerp(this.mobileMedia.matches ? 10 : 22, 0, opening).toFixed(2);
          const productOpacity = lerp(this.mobileMedia.matches ? 0.58 : 0.45, 1, productEntrance).toFixed(3);
          product.style.opacity = productOpacity;
          product.style.visibility = 'visible';
          product.style.clipPath = panel && panel.classList.contains('cp-fresh-panel--horizontal')
            ? `inset(${clipInset}% 0 ${clipInset}% 0)`
            : `inset(0 ${clipInset}% 0 ${clipInset}%)`;
          product.style.transform = `translate3d(0, ${vertical.toFixed(1)}px, ${depth.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
          if (visual) visual.style.opacity = productOpacity;
        }

        if (panel) {
          const offset = definition.direction * opening * 82;
          panel.style.opacity = (0.66 * (1 - opening)).toFixed(3);
          panel.style.visibility = opening > 0.995 ? 'hidden' : 'visible';
          panel.style.transform = panel.classList.contains('cp-fresh-panel--horizontal')
            ? `translate3d(0, ${offset.toFixed(1)}%, 20px)`
            : `translate3d(${offset.toFixed(1)}%, 0, 20px)`;
        }
      });

      this.updateCopySequence(this.freshCopy, progress, 0.28, 0.032, 0.16);
    }

    updateHypeChapter(viewportHeight) {
      if (!this.hypeChapter || !this.hypeItems.length) return;

      const progress = this.chapterProgress(this.hypeChapter, viewportHeight);
      const desktop = [
        { startX: 73, startY: 60, targetX: 68, targetY: 52, startScale: 0.94, targetScale: 1.06, rotation: 2.5, targetZ: 82 },
        { startX: 50, startY: 57, targetX: 56, targetY: 63, startScale: 0.94, targetScale: 0.92, rotation: -2.5, targetZ: 38 },
        { startX: 84, startY: 34, targetX: 79, targetY: 40, startScale: 0.93, targetScale: 0.9, rotation: 2.5, targetZ: 44 },
        { startX: 85, startY: 73, targetX: 81, targetY: 68, startScale: 0.92, targetScale: 0.84, rotation: -2, targetZ: 24 }
      ];
      const mobile = [
        { startX: 52, startY: 71, targetX: 50, targetY: 64, startScale: 0.95, targetScale: 1.05, rotation: 2, targetZ: 64 },
        { startX: 18, startY: 64, targetX: 22, targetY: 70, startScale: 0.95, targetScale: 0.92, rotation: -2, targetZ: 28 },
        { startX: 82, startY: 52, targetX: 78, targetY: 58, startScale: 0.95, targetScale: 0.92, rotation: 2, targetZ: 32 },
        { startX: 85, startY: 76, targetX: 82, targetY: 72, startScale: 0.94, targetScale: 0.86, rotation: -1.5, targetZ: 18 }
      ];
      const definitions = this.mobileMedia.matches ? mobile : desktop;

      this.hypeItems.forEach((item, index) => {
        const definition = definitions[index] || definitions[definitions.length - 1];
        const start = 0.1 + index * 0.038;
        const convergence = easeInOut(mapRange(progress, start, 0.5 + index * 0.025));
        const x = lerp(definition.startX, definition.targetX, convergence);
        const y = lerp(definition.startY, definition.targetY, convergence);
        const scale = lerp(definition.startScale, definition.targetScale, convergence);
        const rotation = lerp(definition.rotation, 0, convergence);
        const depth = lerp(-45, definition.targetZ, convergence);

        item.style.left = `${x.toFixed(2)}%`;
        item.style.top = `${y.toFixed(2)}%`;
        item.style.opacity = lerp(this.mobileMedia.matches ? 0.72 : 0.65, 1, convergence).toFixed(3);
        item.style.visibility = 'visible';
        item.style.transform = `translate3d(-50%, -50%, ${depth.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      });

      this.updateCopySequence(this.hypeCopy, progress, 0.27, 0.03, 0.16);
    }

    updateSetsChapter(viewportHeight) {
      if (!this.setsChapter || !this.setsItems.length) return;

      const progress = this.chapterProgress(this.setsChapter, viewportHeight);
      const desktop = [
        { startX: 21, startY: 49, targetX: 27, targetY: 57, startScale: 0.94, targetScale: 1, rotation: -2.5, targetZ: 72 },
        { startX: 39, startY: 61, targetX: 37, targetY: 53, startScale: 0.95, targetScale: 0.94, rotation: 2, targetZ: 48 },
        { startX: 53, startY: 50, targetX: 47, targetY: 58, startScale: 0.94, targetScale: 0.9, rotation: 2.5, targetZ: 34 }
      ];
      const mobile = [
        { startX: 24, startY: 59, targetX: 30, targetY: 64, startScale: 0.95, targetScale: 1, rotation: -2, targetZ: 58 },
        { startX: 50, startY: 68, targetX: 50, targetY: 61, startScale: 0.96, targetScale: 0.94, rotation: 1.5, targetZ: 40 },
        { startX: 76, startY: 59, targetX: 70, targetY: 65, startScale: 0.95, targetScale: 0.9, rotation: 2, targetZ: 28 }
      ];
      const definitions = this.mobileMedia.matches ? mobile : desktop;

      this.setsItems.forEach((item, index) => {
        const definition = definitions[index] || definitions[definitions.length - 1];
        const start = 0.1 + index * 0.045;
        const assembly = easeInOut(mapRange(progress, start, 0.5 + index * 0.03));
        const x = lerp(definition.startX, definition.targetX, assembly);
        const y = lerp(definition.startY, definition.targetY, assembly);
        const scale = lerp(definition.startScale, definition.targetScale, assembly);
        const rotation = lerp(definition.rotation, 0, assembly);
        const depth = lerp(-55, definition.targetZ, assembly);

        item.style.left = `${x.toFixed(2)}%`;
        item.style.top = `${y.toFixed(2)}%`;
        item.style.opacity = lerp(this.mobileMedia.matches ? 0.76 : 0.68, 1, assembly).toFixed(3);
        item.style.visibility = 'visible';
        item.style.transform = `translate3d(-50%, -50%, ${depth.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      });

      const connection = easeInOut(mapRange(progress, 0.34, 0.58));
      if (this.setsLine) {
        this.setsLine.style.strokeDashoffset = (1 - connection).toFixed(4);
        this.setsLine.style.opacity = lerp(0.24, 1, connection).toFixed(3);
      }

      const cardProgress = easeOut(mapRange(progress, 0.46, 0.66));
      if (this.setsCard) {
        this.setsCard.style.opacity = cardProgress.toFixed(3);
        this.setsCard.style.visibility = cardProgress < 0.01 ? 'hidden' : 'visible';
        this.setsCard.style.transform = `translate3d(-50%, ${lerp(14, 0, cardProgress).toFixed(1)}px, 0)`;
      }

      this.updateCopySequence(this.setsCopy, progress, 0.27, 0.03, 0.16);
    }

    updateGiftChapter(viewportHeight) {
      if (!this.giftChapter || !this.giftItems.length) return;

      const progress = this.chapterProgress(this.giftChapter, viewportHeight);
      const desktop = [
        { start: 0.1, end: 0.54, y: 72, z: -62, scale: 0.94, rotation: 0, drift: -2.5 },
        { start: 0.18, end: 0.6, y: 58, z: -46, scale: 0.95, rotation: -2, drift: 2 },
        { start: 0.24, end: 0.64, y: 52, z: -42, scale: 0.95, rotation: 2, drift: -1.5 }
      ];
      const mobile = [
        { start: 0.12, end: 0.5, y: 34, z: -36, scale: 0.96, rotation: 0, drift: -1.5 },
        { start: 0.2, end: 0.56, y: 28, z: -28, scale: 0.97, rotation: -1.5, drift: 1.5 },
        { start: 0.27, end: 0.61, y: 24, z: -24, scale: 0.97, rotation: 1.5, drift: -1 }
      ];
      const definitions = this.mobileMedia.matches ? mobile : desktop;

      this.giftItems.forEach((item, index) => {
        const definition = definitions[index] || definitions[definitions.length - 1];
        const entrance = easeInOut(mapRange(progress, definition.start, definition.end));
        const vertical = lerp(definition.y, 0, entrance) + (progress - 0.5) * definition.drift;
        const depth = lerp(definition.z, 0, entrance);
        const rotation = lerp(definition.rotation, 0, entrance);
        const scale = lerp(definition.scale, 1, entrance);

        item.style.opacity = lerp(this.mobileMedia.matches ? 0.62 : 0.52, 1, entrance).toFixed(3);
        item.style.visibility = 'visible';
        item.style.transform = `translate3d(-50%, calc(-50% + ${vertical.toFixed(1)}px), ${depth.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      });

      this.giftDecor.forEach((decor, index) => {
        const entrance = easeInOut(mapRange(progress, 0.26 + index * 0.045, 0.7 + index * 0.025));
        const direction = index % 2 === 0 ? -1 : 1;
        decor.style.opacity = (0.48 * entrance).toFixed(3);
        decor.style.visibility = entrance < 0.01 ? 'hidden' : 'visible';
        decor.style.transform = `translate3d(0, ${lerp(18, 0, entrance).toFixed(1)}px, ${lerp(-30, 0, entrance).toFixed(1)}px) rotate(${(direction * lerp(2.5, 0, entrance)).toFixed(2)}deg)`;
      });

      this.updateCopySequence(this.giftCopy, progress, 0.29, 0.032, 0.17);
    }

    updatePathFinalChapter(viewportHeight) {
      if (!this.pathFinalChapter) return;

      const progress = this.chapterProgress(this.pathFinalChapter, viewportHeight);
      const nodeRise = easeOut(mapRange(progress, 0.14, 0.28));
      const nodeSettle = easeInOut(mapRange(progress, 0.28, 0.46));

      if (this.pathFinalNode) {
        const nodeScale = nodeSettle > 0
          ? lerp(1.2, 1, nodeSettle)
          : lerp(1, 1.2, nodeRise);
        this.pathFinalNode.style.transform = `translate(-50%, -50%) scale(${nodeScale.toFixed(3)})`;
      }

      if (this.pathFinalContent) {
        this.pathFinalContent.style.opacity = '1';
        this.pathFinalContent.style.visibility = 'visible';
        this.pathFinalContent.style.transform = 'translate(-50%, 0)';
      }

      const timings = [
        [0.18, 0.34],
        [0.24, 0.44],
        [0.34, 0.54],
        [0.42, 0.62]
      ];

      this.pathFinalParts.forEach((part, index) => {
        const timing = timings[Math.min(index, timings.length - 1)];
        const entrance = easeOut(mapRange(progress, timing[0], timing[1]));
        part.style.opacity = entrance.toFixed(3);
        part.style.visibility = entrance < 0.01 ? 'hidden' : 'visible';
        part.style.transform = `translate3d(0, ${lerp(this.mobileMedia.matches ? 16 : 24, 0, entrance).toFixed(1)}px, 0)`;
        if (part.matches('a')) part.style.pointerEvents = entrance < 0.5 ? 'none' : 'auto';
      });
    }

    clearMotionStyles() {
      const clear = (elements, properties) => {
        elements.filter(Boolean).forEach((element) => {
          properties.forEach((property) => element.style.removeProperty(property));
        });
      };
      const copies = [this.iconsCopy, this.freshCopy, this.hypeCopy, this.setsCopy, this.giftCopy];
      const copyParts = copies.flatMap((copy) => copy ? [...copy.children] : []);

      clear([this.hero, this.final, this.node, this.scrollCue], ['opacity', 'transform', 'visibility']);
      clear([this.backgroundOverlay, this.backgroundGrain], ['opacity']);
      clear([this.path, this.curatedPath, this.setsLine], ['stroke-dashoffset', 'opacity']);
      clear(this.products, ['left', 'top', 'opacity', 'transform', 'visibility']);
      this.products.forEach((product) => {
        product.classList.remove('is-excluded');
        product.removeAttribute('aria-hidden');
        delete product.dataset.cpResolvedOutcome;
        delete product.dataset.cpResolvedFinalSlot;
      });
      this.excludedProducts.clear();
      clear([this.stageShell], ['opacity', 'transform', 'visibility']);
      clear(this.cards, ['opacity', 'transform', 'visibility']);
      clear(this.chapters.map((chapter) => chapter.querySelector('.cp-chapter__node')), ['transform', '--cp-node-ring-opacity', '--cp-node-halo-opacity']);
      clear(copies, ['opacity', 'transform', 'visibility']);
      clear(copyParts, ['opacity', 'transform', 'visibility', 'pointer-events']);
      clear(this.iconsProducts, ['opacity', 'transform', 'visibility']);
      clear(this.iconsProducts.map((product) => product.firstElementChild), ['opacity', 'visibility']);
      clear(this.freshSlots.flatMap((slot) => [
        slot.querySelector('[data-cp-fresh-product]'),
        slot.querySelector('[data-cp-fresh-panel]')
      ]), ['opacity', 'transform', 'visibility', 'clip-path']);
      clear(this.freshSlots.map((slot) => {
        const product = slot.querySelector('[data-cp-fresh-product]');
        return product ? product.firstElementChild : null;
      }), ['opacity']);
      clear(this.hypeItems, ['left', 'top', 'opacity', 'transform', 'visibility']);
      clear(this.setsItems, ['left', 'top', 'opacity', 'transform', 'visibility']);
      clear([this.setsCard], ['opacity', 'transform', 'visibility']);
      clear(this.giftItems, ['opacity', 'transform', 'visibility']);
      clear(this.giftDecor, ['opacity', 'transform', 'visibility']);
      clear([this.pathFinalNode, this.pathFinalContent], ['opacity', 'transform', 'visibility']);
      clear(this.pathFinalParts, ['opacity', 'transform', 'visibility', 'pointer-events']);

      this.root.style.removeProperty('--cp-progress');
      if (this.pathExperience) {
        [
          '--cp-path-progress',
          '--cp-world-cool',
          '--cp-world-energy',
          '--cp-world-structure',
          '--cp-world-warm',
          '--cp-world-clear',
          '--cp-world-cool-opacity',
          '--cp-world-warm-opacity',
          '--cp-world-depth-opacity',
          '--cp-world-light-opacity',
          '--cp-world-light-x',
          '--cp-world-shift-x',
          '--cp-world-shift-y',
          '--cp-world-depth-x',
          '--cp-world-depth-y'
        ].forEach((property) => this.pathExperience.style.removeProperty(property));
      }
      this.chapters.forEach((chapter) => chapter.classList.remove('is-active', 'is-path-reached'));
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
        this.usesScrollTrigger = false;
      }

      if (this.heroTimeline) {
        this.heroTimeline.kill();
        this.heroTimeline = null;
        this.timelineClock = null;
      }

      this.scroller.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      const remove = (query, handler) => {
        if (query.removeEventListener) query.removeEventListener('change', handler);
        else query.removeListener(handler);
      };
      remove(this.mediaReduced, this.handleMotionChange);
      remove(this.mobileMedia, this.handleResize);

      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);

      this.clearMotionStyles();
      this.root.classList.remove('is-ready', 'is-reduced-motion', 'is-scrolltrigger-driven');
      instances.delete(this.root);
    }
  }

  /* Desktop journey. Native document positions remain the source of truth for
   * scrollbar/history navigation; wheel/key gestures request one adjacent state.
   * No GSAP driver is mounted alongside this controller. */
  class SpatialCuratedPicks extends CuratedPicks {
    init() {
      this.currentScene = 0;
      this.targetScene = 0;
      this.transitionProgress = 1;
      this.phase = 'idle';
      this.camera = { x: 0, y: 0 };
      this.gesture = { lastAt: -Infinity, direction: 0, sum: 0, consumed: false };
      this.restores = [];
      this.listeners = [];
      this.sceneDefinitions = [
        { id: 'entrance', x: 0, y: 0, element: this.track },
        { id: 'icons', x: 1.12, y: 0, element: this.iconsChapter },
        { id: 'fresh', x: 1.15, y: -1.14, element: this.freshChapter },
        { id: 'hype', x: 2.40, y: -1.12, element: this.hypeChapter },
        { id: 'sets', x: 2.45, y: 0.20, element: this.setsChapter },
        { id: 'gift', x: 3.68, y: 0.02, element: this.giftChapter }
      ];
      this.buildSpatialWorld();
      this.bindMediaEvents();
      this.listen(window, 'resize', this.handleResize, { passive: true });
      this.listen(this.scroller, 'scroll', this.handleScroll, { passive: true });
      this.listen(this.scroller, 'wheel', (event) => this.onWheel(event), { passive: false });
      this.listen(document, 'keydown', (event) => this.onKey(event));
      this.listen(document, 'pointerdown', (event) => {
        const gutter = this.scroller === window
          ? event.clientX >= document.documentElement.clientWidth
          : event.clientX >= this.scroller.getBoundingClientRect().right - 18;
        if (gutter) {
          this.draggingScrollbar = true;
          this.cancelTransition();
        }
      });
      this.listen(window, 'pointerup', () => {
        if (!this.draggingScrollbar) return;
        this.draggingScrollbar = false;
        this.scheduleNativeArrival();
      });
      this.listen(this.root, 'load', (event) => {
        if (event.target.tagName === 'IMG') this.handleResize();
      }, true);
      this.listen(this.root, 'focusin', (event) => {
        // Tabbing out is always native. Inactive panels are inert.
        if (this.pathFinalChapter?.contains(event.target)) this.cancelTransition();
      });
      this.listen(document, 'shopify:block:select', (event) => {
        const index = this.sceneDefinitions.findIndex((scene) => scene.element.contains(event.target));
        if (index >= 0) this.goTo(index);
      });
      this.measure();
      const position = clamp((this.readScroll() - this.start) / this.stride, 0, 5);
      this.currentScene = this.targetScene = Math.round(position);
      this.paintPosition(position);
      this.activate(this.currentScene);
      this.root.classList.add('is-ready');
      if (this.inJourney() && Math.abs(position - Math.round(position)) > 0.002) this.scheduleNativeArrival();
      if (document.fonts?.ready) document.fonts.ready.then(() => {
        if (!this.destroyed) this.handleResize();
      });
    }

    listen(target, type, callback, options) {
      target.addEventListener(type, callback, options);
      this.listeners.push(() => target.removeEventListener(type, callback, options));
    }

    move(element, parent) {
      if (!element) return;
      const marker = document.createComment('cp-original-position');
      element.before(marker);
      this.restores.push(() => marker.replaceWith(element));
      parent.append(element);
    }

    buildSpatialWorld() {
      this.root.classList.add('is-spatial');
      this.spatialTrack = document.createElement('div');
      this.spatialTrack.className = 'cp-spatial-track';
      this.viewport = document.createElement('div');
      this.viewport.className = 'cp-spatial-viewport';
      this.world = document.createElement('div');
      this.world.className = 'cp-spatial-world';
      this.root.prepend(this.spatialTrack);
      this.spatialTrack.append(this.viewport);
      this.move(this.pathExperience.querySelector('.cp-path-background'), this.viewport);
      this.viewport.append(this.world);
      this.move(this.track, this.world);
      this.move(this.pathExperience, this.world);
      // Keep the existing closing copy as normal-flow content after Gift Edit.
      this.move(this.pathFinalChapter, this.root);
      this.panels = this.sceneDefinitions.map((scene) => scene.element);
      this.originalNodePositions = this.panels.map((panel) => panel.style.getPropertyValue('--cp-node-x'));
      this.contentFrames = this.panels.slice(1).map((panel) => {
        const frame = document.createElement('div');
        frame.className = 'cp-spatial-content';
        // The route node stays in world coordinates, outside the fitted composition.
        [...panel.children].filter((child) => !child.classList.contains('cp-chapter__node')).forEach((child) => frame.append(child));
        panel.append(frame);
        return frame;
      });
      this.panels.forEach((panel, index) => {
        panel.classList.add('cp-spatial-panel');
        panel.dataset.cpScene = this.sceneDefinitions[index].id;
      });
      this.route = this.pathExperience.querySelector('.cp-curated-path');
      this.originalRoute = this.route.cloneNode(true);
      this.route.replaceChildren();
      this.routeSegments = [];
      for (let i = 0; i < 5; i++) {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('class', 'cp-curated-path__base cp-spatial-route');
        this.route.append(path);
        this.routeSegments.push(path);
      }
      this.status = document.createElement('span');
      this.status.className = 'cp-spatial-status';
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      this.viewport.append(this.status);
      // Settle existing product compositions once, then reveal their containers
      // on arrival. Their old per-scroll drift is never run during travel.
      this.update(0);
      ['Icons', 'Fresh', 'Hype', 'Sets', 'Gift', 'PathFinal'].forEach((name) => this[`update${name}Chapter`](0));
      this.compositions = this.panels.map((panel) => panel.querySelector(
        '.cp-icons-composition, .cp-fresh-composition, .cp-hype-composition, .cp-sets-composition, .cp-gift-composition'));
    }

    chapterProgress() { return 1; }
    readScroll() { return this.scroller === window ? window.scrollY : this.scroller.scrollTop; }
    writeScroll(y) {
      this.expectedScroll = y;
      this.scroller.scrollTo({ top: y, behavior: 'instant' });
    }
    inJourney(y = this.readScroll()) { return y >= this.start - 1 && y <= this.end + 1; }

    measure() {
      const y = this.readScroll();
      const viewportTop = this.scrollViewportTop();
      this.width = this.root.clientWidth;
      this.height = this.scrollViewportHeight();
      this.topInset = Math.max(0, parseFloat(getComputedStyle(this.root).getPropertyValue('--cp-sticky-top')) || 0);
      this.height = Math.max(240, this.height - this.topInset);
      this.stride = Math.max(480, this.height * 0.90);
      this.root.style.setProperty('--cp-viewport-height', `${this.height}px`);
      this.spatialTrack.style.height = `${this.height + 5 * this.stride}px`;
      this.start = this.spatialTrack.getBoundingClientRect().top - viewportTop + y - this.topInset;
      this.end = this.start + this.stride * 5;
      this.sceneStates = this.sceneDefinitions.map((scene, index) => ({
        ...scene, x: scene.x * this.width, y: scene.y * this.height,
        scroll: this.start + index * this.stride, scale: 1
      }));
      this.sceneStates.forEach((scene) => {
        scene.element.style.transform = `translate3d(${scene.x}px, ${scene.y}px, 0)`;
      });
      this.fitCompositions();
      this.buildRoute();
    }

    fitCompositions() {
      // Read final title/product bounds once per layout pass. Fit the complete
      // editorial grouping, retaining the asymmetry of the original composition.
      this.contentFrames.forEach((frame) => { frame.style.transform = 'none'; });
      const bounds = this.contentFrames.map((frame) => {
        const panel = frame.parentElement.getBoundingClientRect();
        const boxes = [...frame.querySelectorAll('.cp-chapter__content, [data-cp-icons-product], [data-cp-fresh-slot], [data-cp-hype-item], [data-cp-sets-item], [data-cp-sets-card], [data-cp-gift-item]')]
          .map((element) => element.getBoundingClientRect()).filter((box) => box.width && box.height);
        if (!boxes.length) return null;
        return { left: Math.min(...boxes.map((b) => b.left)) - panel.left,
          right: Math.max(...boxes.map((b) => b.right)) - panel.left,
          top: Math.min(...boxes.map((b) => b.top)) - panel.top,
          bottom: Math.max(...boxes.map((b) => b.bottom)) - panel.top };
      });
      bounds.forEach((box, index) => {
        if (!box) return;
        const scale = Math.min(1, this.width * .90 / (box.right - box.left), this.height * .82 / (box.bottom - box.top));
        const x = this.width * .50 - (box.left + box.right) * .5 * scale;
        const y = this.height * .50 - (box.top + box.bottom) * .5 * scale;
        this.contentFrames[index].style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      });
    }

    buildRoute() {
      // Route anchors use the title side. Transit sections stay in the gutters.
      // A mask excludes product and copy bounds, even when configured images grow.
      const w = this.width, h = this.height;
      const anchors = this.sceneStates.map((scene, i) => ({
        x: scene.x + (i === 2 || i === 4 ? 0.96 : 0.035) * w,
        y: scene.y + 0.50 * h
      }));
      this.panels.slice(1).forEach((panel, i) => {
        panel.style.setProperty('--cp-node-x', i === 1 || i === 3 ? '96%' : '3.5%');
      });
      this.route.setAttribute('viewBox', `${-w} ${-2*h} ${7*w} ${5*h}`);
      this.route.style.cssText = `left:${-w}px;top:${-2*h}px;width:${7*w}px;height:${5*h}px`;
      const ns = 'http://www.w3.org/2000/svg';
      this.route.querySelector('defs')?.remove();
      const defs = document.createElementNS(ns, 'defs');
      const mask = document.createElementNS(ns, 'mask');
      const maskId = `cp-route-mask-${this.root.id}`;
      mask.id = maskId;
      mask.setAttribute('maskUnits', 'userSpaceOnUse');
      mask.setAttribute('x', -w); mask.setAttribute('y', -2*h);
      mask.setAttribute('width', 7*w); mask.setAttribute('height', 5*h);
      const rect = (x, y, width, height, fill) => {
        const node = document.createElementNS(ns, 'rect');
        Object.entries({x,y,width,height,fill}).forEach(([key, value]) => node.setAttribute(key, value));
        mask.append(node);
      };
      rect(-w, -2*h, 7*w, 5*h, 'white');
      // Bounds reads occur only at layout/resize, never in the animation loop.
      this.sceneStates.slice(1).forEach((scene) => {
        const panelRect = scene.element.getBoundingClientRect();
        const protectedElements = scene.element.querySelectorAll(
          '.cp-chapter__content, [data-cp-icons-product], [data-cp-fresh-slot], [data-cp-hype-item], [data-cp-sets-item], [data-cp-sets-card], [data-cp-gift-item], [data-cp-gift-decor]');
        protectedElements.forEach((element) => {
          const box = element.getBoundingClientRect();
          rect(scene.x + box.left - panelRect.left - 24, scene.y + box.top - panelRect.top - 24,
            box.width + 48, box.height + 48, 'black');
        });
      });
      defs.append(mask); this.route.prepend(defs);
      this.routeSegments.forEach((path, i) => {
        const a = anchors[i], b = anchors[i + 1];
        const vertical = i === 1 || i === 3;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        path.setAttribute('d', vertical
          ? `M${a.x},${a.y} C${a.x},${midY} ${b.x},${midY} ${b.x},${b.y}`
          : `M${a.x},${a.y} C${midX},${a.y} ${midX},${b.y} ${b.x},${b.y}`);
        path.setAttribute('mask', `url(#${maskId})`);
      });
    }

    paintCamera(x, y) {
      this.camera = { x, y };
      this.world.style.transform = `translate3d(${-x}px, ${-y}px, 0)`;
      // Tiny backdrop drift is driven by the same camera, not an independent loop.
      this.viewport.style.setProperty('--cp-world-shift-x', `${-x * 0.018}px`);
      this.viewport.style.setProperty('--cp-world-shift-y', `${-y * 0.025}px`);
    }

    paintPosition(position) {
      const from = Math.floor(position), to = Math.min(5, from + 1);
      const progress = position - from;
      this.paintCamera(lerp(this.sceneStates[from].x, this.sceneStates[to].x, progress),
        lerp(this.sceneStates[from].y, this.sceneStates[to].y, progress));
      this.update(clamp(position));
    }

    activate(index, reveal = true) {
      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
        panel.classList.toggle('is-arrived', i === index && reveal);
        panel.classList.toggle('is-path-reached', i <= index);
        panel.inert = i !== index;
        panel.setAttribute('aria-hidden', String(i !== index));
      });
      this.routeSegments.forEach((path, i) => path.classList.toggle('is-reached', i < index));
      this.root.dataset.cpActiveScene = this.sceneDefinitions[index].id;
      this.root.dataset.cpPhase = this.phase;
      if (reveal) this.status.textContent = this.panels[index].querySelector('h2, h3')?.textContent || '';
    }

    cancelTransition() {
      if (this.motionRaf) cancelAnimationFrame(this.motionRaf);
      clearTimeout(this.settleTimer);
      clearTimeout(this.nativeTimer);
      this.motionRaf = null;
      this.phase = 'idle';
      this.transitionProgress = 1;
      this.root.dataset.cpPhase = 'idle';
      this.viewport?.classList.remove('is-travelling');
    }

    goTo(index) {
      index = clamp(index, 0, 5);
      this.cancelTransition();
      this.targetScene = index;
      this.phase = 'travelling';
      this.transitionProgress = 0;
      this.root.dataset.cpPhase = this.phase;
      this.viewport.classList.add('is-travelling');
      this.panels.forEach((panel) => { panel.classList.remove('is-arrived'); panel.inert = true; });
      // Shift focus off a departing link without scrolling it back into view.
      if (this.world.contains(document.activeElement)) document.activeElement.blur();
      const from = { ...this.camera }, to = this.sceneStates[index];
      const fromScroll = this.readScroll();
      const distance = Math.hypot((to.x - from.x) / this.width, (to.y - from.y) / this.height);
      const duration = clamp(distance * 780, 320, 1050);
      const startTime = performance.now();
      const frame = (now) => {
        if (this.destroyed) return;
        const t = clamp((now - startTime) / duration);
        const e = t*t*t*(t*(t*6 - 15) + 10); // zero speed AND acceleration at both ends
        this.transitionProgress = t;
        this.paintCamera(lerp(from.x, to.x, e), lerp(from.y, to.y, e));
        this.writeScroll(lerp(fromScroll, to.scroll, e));
        // Preserve the hero's existing selection sequence on the entrance leg.
        if (index === 0 || this.currentScene === 0) this.update(index === 0 ? 1 - e : e);
        if (t < 1) this.motionRaf = requestAnimationFrame(frame);
        else this.arrive(index);
      };
      this.motionRaf = requestAnimationFrame(frame);
    }

    arrive(index) {
      this.motionRaf = null;
      this.currentScene = this.targetScene = index;
      this.transitionProgress = 1;
      const state = this.sceneStates[index];
      this.paintCamera(state.x, state.y);
      this.writeScroll(state.scroll);
      this.update(index === 0 ? 0 : 1);
      // No integration velocity or queued deltas survive arrival.
      this.gesture.sum = 0;
      this.phase = 'settling';
      this.viewport.classList.remove('is-travelling');
      this.activate(index);
      this.settleTimer = setTimeout(() => {
        this.phase = 'idle'; this.root.dataset.cpPhase = 'idle';
        if (this.pendingLayout) { this.pendingLayout = false; this.handleResize(); }
      }, index === 0 ? 80 : 380);
    }

    allowsNative(event, direction) {
      if (event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return true;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], dialog, [role="dialog"], [data-cp-native-scroll]')) return true;
      // Header drawers, carts and unrelated overlays must retain native input.
      if (target && !this.root.contains(target) && !this.spatialTrack.contains(target)) return true;
      let node = target;
      while (node && node !== this.root && node !== document.body) {
        const styles = getComputedStyle(node);
        if (/(auto|scroll)/.test(styles.overflowY) && node.scrollHeight > node.clientHeight + 1 &&
          (direction < 0 ? node.scrollTop > 0 : node.scrollTop < node.scrollHeight - node.clientHeight - 1)) return true;
        node = node.parentElement;
      }
      return false;
    }

    onWheel(event) {
      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (!delta || !event.cancelable) return;
      const direction = Math.sign(delta);
      if (!this.inJourney() || this.allowsNative(event, direction)) return;
      const now = performance.now();
      const gesture = this.gesture;
      const reversed = direction !== gesture.direction;
      const fresh = now - gesture.lastAt > 190 || reversed;
      if (fresh) { gesture.sum = 0; gesture.consumed = false; }
      gesture.lastAt = now; gesture.direction = direction;
      const endpoint = direction < 0 ? this.currentScene === 0 : this.currentScene === 5;
      if (endpoint && this.phase === 'idle' && !gesture.consumed) return;
      event.preventDefault();
      if (gesture.consumed) return;
      const normalized = Math.abs(delta) * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.height : 1);
      gesture.sum += Math.min(normalized, 120);
      if (gesture.sum < 18) return;
      if (this.phase === 'travelling') {
        const travelDirection = Math.sign(this.targetScene - this.currentScene);
        if (direction !== travelDirection && travelDirection !== 0) {
          gesture.consumed = true;
          this.goTo(this.currentScene); // reverse from the actual camera position
        } else gesture.consumed = true;
        return;
      }
      if (this.phase === 'settling') { gesture.consumed = true; return; }
      gesture.consumed = true;
      this.goTo(this.currentScene + direction);
    }

    onKey(event) {
      if (!this.inJourney() || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input, textarea, select, button, a, [contenteditable="true"], dialog, [role="dialog"]')) return;
      const direction = ['ArrowDown','ArrowRight','PageDown',' '].includes(event.key) ? (event.shiftKey ? -1 : 1)
        : ['ArrowUp','ArrowLeft','PageUp'].includes(event.key) ? -1 : 0;
      if (!direction) return; // Home/End/Tab/Escape retain browser semantics.
      if (this.phase === 'idle' && (this.currentScene === 0 && direction < 0 || this.currentScene === 5 && direction > 0)) return;
      event.preventDefault();
      if (event.repeat || this.phase !== 'idle') return;
      this.gesture.consumed = true;
      this.goTo(this.currentScene + direction);
    }

    handleScroll() {
      if (this.destroyed || !this.sceneStates) return;
      const y = this.readScroll();
      if (this.expectedScroll !== undefined && Math.abs(y - this.expectedScroll) < 2) return;
      this.expectedScroll = undefined;
      // An external scroll (scrollbar, history, touch, Home/End) takes ownership.
      this.cancelTransition();
      const position = clamp((y - this.start) / this.stride, 0, 5);
      this.paintPosition(position);
      this.currentScene = this.targetScene = Math.round(position);
      this.phase = this.inJourney(y) ? 'scrubbing' : 'idle';
      const atAnchor = Math.abs(position - Math.round(position)) < 0.002;
      this.activate(this.currentScene, atAnchor);
      if (this.inJourney(y)) this.scheduleNativeArrival();
      else { this.gesture.sum = 0; this.gesture.consumed = false; }
    }

    scheduleNativeArrival() {
      clearTimeout(this.nativeTimer);
      if (this.draggingScrollbar) return;
      this.nativeTimer = setTimeout(() => {
        if (!this.inJourney() || this.destroyed) return;
        const index = Math.round(clamp((this.readScroll() - this.start) / this.stride, 0, 5));
        const state = this.sceneStates[index];
        if (Math.abs(this.readScroll() - state.scroll) < 1) {
          this.phase = 'idle'; this.activate(index); return;
        }
        this.goTo(index);
      }, 150);
    }

    handleResize() {
      if (this.destroyed || !this.sceneStates) return;
      if (this.mobileMedia.matches || this.mediaReduced.matches) {
        const root = this.root; this.destroy(); initSection(root); return;
      }
      if (this.phase === 'travelling' || this.phase === 'settling') { this.pendingLayout = true; return; }
      if (this.layoutRaf) cancelAnimationFrame(this.layoutRaf);
      this.layoutRaf = requestAnimationFrame(() => {
        this.layoutRaf = null;
        const wasInside = this.inJourney();
        const target = this.targetScene;
        this.cancelTransition();
        this.measure();
        this.currentScene = this.targetScene = target;
        this.paintPosition(target);
        this.activate(target);
        if (wasInside) this.writeScroll(this.sceneStates[target].scroll);
      });
    }

    destroy() {
      if (this.destroyed) return;
      this.cancelTransition();
      if (this.layoutRaf) cancelAnimationFrame(this.layoutRaf);
      this.listeners.forEach((remove) => remove());
      this.panels.forEach((panel, index) => {
        panel.inert = false; panel.removeAttribute('aria-hidden');
        panel.classList.remove('cp-spatial-panel','is-arrived');
        panel.style.removeProperty('transform');
        if (this.originalNodePositions[index]) panel.style.setProperty('--cp-node-x', this.originalNodePositions[index]);
        else panel.style.removeProperty('--cp-node-x');
        delete panel.dataset.cpScene;
      });
      this.contentFrames.forEach((frame) => frame.replaceWith(...frame.children));
      // Restore the original Liquid hierarchy for mobile/reduced motion/editor reload.
      this.route.replaceWith(this.originalRoute);
      this.restores.reverse().forEach((restore) => restore());
      this.spatialTrack.remove();
      this.root.classList.remove('is-spatial');
      this.root.style.removeProperty('--cp-viewport-height');
      delete this.root.dataset.cpActiveScene; delete this.root.dataset.cpPhase;
      super.destroy();
    }
  }

  function initSection(root) {
    if (!root || instances.has(root)) return;
    const spatial = root.dataset.cpMovement === 'spatial'
      && !window.matchMedia('(max-width: 989px), (prefers-reduced-motion: reduce)').matches;
    instances.set(root, spatial ? new SpatialCuratedPicks(root) : new CuratedPicks(root));
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(SELECTOR)) initSection(scope);
    scope.querySelectorAll(SELECTOR).forEach(initSection);
  }

  function reinitAll(scope) {
    const roots = [];
    if (scope.matches && scope.matches(SELECTOR)) roots.push(scope);
    scope.querySelectorAll(SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
      initSection(root);
    });
  }

  function destroyWithin(scope) {
    const roots = [];
    if (scope.matches && scope.matches(SELECTOR)) roots.push(scope);
    scope.querySelectorAll(SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => reinitAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  window.TotokaCuratedPicks = {
    init: initAll,
    destroy: destroyWithin,
    getState(root = document.querySelector(SELECTOR)) {
      const instance = instances.get(root);
      if (!instance?.sceneStates) return null;
      return { currentScene: instance.currentScene, targetScene: instance.targetScene,
        phase: instance.phase, transitionProgress: instance.transitionProgress,
        camera: { ...instance.camera }, start: instance.start, end: instance.end,
        sceneStates: instance.sceneStates.map(({ id, x, y, scroll, scale }) => ({ id, x, y, scroll, scale })) };
    },
    refresh() {
      instances.forEach((instance) => instance.handleResize());
    }
  };
})();
